import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Clock, 
    Bell, 
    Laptop, 
    Settings, 
    CheckCircle2, 
    Mail, 
    BellRing, 
    ChevronRight, 
    Plus, 
    Shield, 
    Trash2,
    Calendar,
    Target,
    Globe,
    Layers,
    Smartphone,
    Save,
    Info,
    Edit3,
    X,
    Search,
    Briefcase
} from 'lucide-react';
import './OnboardingConfiguration.css';
import Swal from 'sweetalert2';

// Import Redux Actions
import { fetchOnboardingConfig, updateOnboardingConfig } from '../../../redux/actions/onboardingConfigActions';
import { getAllJobsRequest } from '../../../redux/actions/Jobactions';
import { fetchParcoursRequest } from '../../../redux/actions/parcoursActions';

// Static Defaults in case DB or state is empty
const DEFAULT_ALERTS = [
    { id: 1, event: 'END_OF_PATH', notifyManager: true, notifyHR: true, notifyBuddy: false, viaEmail: true, viaNotification: true },
    { id: 2, event: 'MODULE_END', notifyManager: false, notifyHR: false, notifyBuddy: true, viaEmail: false, viaNotification: true }
];

const DEFAULT_KITS = [
    { id: 1, name: "Kit Développeur", responsible: "Support IT", email: "it@entreprise.com", items: "MacBook Pro, Écran externe, Souris" },
    { id: 2, name: "Kit Standard", responsible: "Services Généraux", email: "sg@entreprise.com", items: "PC Portable, Badge, Carnet" }
];

const OnboardingConfiguration = () => {
    const dispatch = useDispatch();
    
    // Selectors from Redux Store
    const { config, loading } = useSelector(state => state.onboardingConfig || { config: null, loading: false });
    const { jobs: jobsList = [] } = useSelector(state => state.jobs || {});
    const { list: parcoursList = [] } = useSelector(state => state.parcours || {});
    
    // UI States
    const [activeTab, setActiveTab] = useState('duration');
    const [selectedKitId, setSelectedKitId] = useState(null);
    const [kitSearchQuery, setKitSearchQuery] = useState('');
    const [ruleSearchQuery, setRuleSearchQuery] = useState(''); // Search query for parcours or jobs rules
    
    // Local form states
    const [localSettings, setLocalSettings] = useState({ durationMode: 'GENERAL', defaultDuration: 30, stagnationDelay: 3 });
    const [durationRules, setDurationRules] = useState([]);
    const [alerts, setAlerts] = useState(DEFAULT_ALERTS);
    const [kits, setKits] = useState(DEFAULT_KITS);

    // Initial data fetch
    useEffect(() => {
        dispatch(fetchOnboardingConfig());
        dispatch(getAllJobsRequest());
        dispatch(fetchParcoursRequest());
    }, [dispatch]);

    // Populate local states when config is successfully fetched from DB
    useEffect(() => {
        if (config) {
            setLocalSettings({
                durationMode: config.durationMode || 'GENERAL',
                defaultDuration: config.defaultDuration || 30,
                stagnationDelay: config.stagnationDelay || 3
            });
            setDurationRules(config.durationRules || []);
            setAlerts(config.alerts || DEFAULT_ALERTS);
            setKits(config.kits || DEFAULT_KITS);
            
            // Default select the first kit in list if any
            const loadedKits = config.kits || DEFAULT_KITS;
            if (loadedKits.length > 0) {
                setSelectedKitId(loadedKits[0].id);
            }
        }
    }, [config]);

    // Triggered when clicking the main primary save button on the page header
    const handleSaveSettings = () => {
        const payload = {
            durationMode: localSettings.durationMode,
            defaultDuration: parseInt(localSettings.defaultDuration) || 30,
            stagnationDelay: parseInt(localSettings.stagnationDelay) || 3,
            durationRules,
            alerts,
            kits
        };
        dispatch(updateOnboardingConfig(payload));
    };

    const handleModeChange = (mode) => {
        setLocalSettings({ ...localSettings, durationMode: mode });
        setRuleSearchQuery(''); // Reset search input when toggling calculation modes
    };

    // Save/update duration rules in local state
    const handleRuleSave = (type, refId, duration) => {
        const parsedDuration = parseInt(duration) || 30;
        
        // Find existing rule
        const existingIndex = durationRules.findIndex(r => 
            (type === 'POSITION' && r.job?.id === refId) || 
            (type === 'PATH' && r.parcours?.id === refId)
        );

        const newRule = { 
            id: existingIndex >= 0 ? durationRules[existingIndex].id : Date.now(), 
            duration: parsedDuration 
        };
        
        if (type === 'POSITION') newRule.job = { id: refId };
        if (type === 'PATH') newRule.parcours = { id: refId };

        if (existingIndex >= 0) {
            const updatedRules = [...durationRules];
            updatedRules[existingIndex] = newRule;
            setDurationRules(updatedRules);
        } else {
            setDurationRules([...durationRules, newRule]);
        }
    };

    const handleAlertToggle = (alertId, field, value) => {
        setAlerts(alerts.map(alert => 
            alert.id === alertId ? { ...alert, [field]: value } : alert
        ));
    };

    // Logistics kits CRUD handlers
    const handleAddKit = () => {
        const newKit = {
            id: Date.now(),
            name: "Nouveau Kit Logistique",
            responsible: "Support IT",
            email: "support@votreentreprise.com",
            items: "Ordinateur, Badge, Souris"
        };
        setKits([...kits, newKit]);
        setSelectedKitId(newKit.id);
    };

    const handleKitFieldChange = (field, value) => {
        setKits(kits.map(kit => kit.id === selectedKitId ? { ...kit, [field]: value } : kit));
    };

    const handleDeleteKit = (id) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Ce kit sera définitivement retiré des configurations locales.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ee645f',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler',
            customClass: {
                confirmButton: 'oc-btn oc-btn-accent',
                cancelButton: 'oc-btn oc-btn-ghost'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                const remainingKits = kits.filter(kit => kit.id !== id);
                setKits(remainingKits);
                if (selectedKitId === id) {
                    setSelectedKitId(remainingKits.length > 0 ? remainingKits[0].id : null);
                }
            }
        });
    };

    const tabs = [
        { id: 'duration', label: 'Durée & Parcours', icon: <Clock size={14} /> },
        { id: 'alerts', label: 'Alertes & Suivi', icon: <BellRing size={14} /> },
        { id: 'equipment', label: 'Outils & Logistique', icon: <Laptop size={14} /> },
    ];

    // Filter kits by search query
    const filteredKits = kits.filter(k => 
        k.name.toLowerCase().includes(kitSearchQuery.toLowerCase()) ||
        k.responsible.toLowerCase().includes(kitSearchQuery.toLowerCase()) ||
        k.items.toLowerCase().includes(kitSearchQuery.toLowerCase())
    );

    // Filter Parcours duration rules list
    const filteredParcours = parcoursList.filter(p => 
        p.nom.toLowerCase().includes(ruleSearchQuery.toLowerCase())
    );

    // Filter Jobs/Postes duration rules list
    const filteredJobs = jobsList.filter(job => 
        job.name.toLowerCase().includes(ruleSearchQuery.toLowerCase())
    );

    const selectedKit = kits.find(k => k.id === selectedKitId);

    if (loading && Object.keys(localSettings).length === 0) {
        return (
            <div className="oc-page">
                <div className="oc-loader-container">
                    <div className="oc-spinner"></div>
                    <span style={{ fontWeight: 600, fontSize: '13px' }}>Chargement de la configuration...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="oc-page">
            {/* Header */}
            <div className="oc-header">
                <div className="oc-header-left">
                    <h1>Configuration général de l'onboarding</h1>
                    <p className="oc-subtitle">Configurez les délais d'intégration, les alertes automatiques et la logistique d'arrivée</p>
                </div>
                <div className="oc-header-actions">
                    <button 
                        className="oc-btn oc-btn-primary" 
                        onClick={handleSaveSettings}
                        disabled={loading}
                    >
                        <Save size={13} />
                        <span>{loading ? 'Enregistrement...' : 'Enregistrer la configuration'}</span>
                    </button>
                </div>
            </div>

            {/* Sub-Info Bar */}
            <div className="oc-info-bar">
                <div className="oc-info-icon"><Info size={14} /></div>
                <div className="oc-info-text">
                    Modifiez les paramètres ci-dessous en toute liberté. Pensez à cliquer sur <strong>Enregistrer la configuration</strong> en haut à droite pour valider définitivement vos modifications.
                </div>
            </div>

            {/* Split Page Layout (Navigation Sidebar + Content Panel) */}
            <div className="oc-layout-split">
                
                {/* Left Column: Vertical Tabs Sidebar */}
                <div className="oc-sidebar-nav">
                    <span style={{ fontSize: '9px', fontWeight: 800, color: 'var(--oc-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '2px 14px 6px 14px', borderBottom: '1px solid #f3f4f6', marginBottom: '6px' }}>
                        PARAMÉTRAGES GENERAL
                    </span>
                    
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={`oc-tab-vertical ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <div className="oc-tab-vertical-border" />
                            <div className="oc-tab-vertical-icon">
                                {tab.icon}
                            </div>
                            <span>{tab.label}</span>
                        </button>
                    ))}
                    
                    {/* Compact info box at the bottom of the navigation sidebar */}
                    <div className="oc-sidebar-footer-info">
                        <div style={{ display: 'flex', gap: '6px', color: 'var(--oc-primary)', marginBottom: '2px', alignItems: 'center' }}>
                            <Info size={12} style={{ flexShrink: 0 }} />
                            <span style={{ fontSize: '10px', fontWeight: 700 }}>Rappel</span>
                        </div>
                        <p style={{ margin: 0, fontSize: '9px', color: '#6b7280', lineHeight: 1.3, fontWeight: 500 }}>
                            Ces règles s'appliquent automatiquement à chaque nouveau collaborateur lors de son embauche.
                        </p>
                    </div>
                </div>

                {/* Right Column: Active Tab Content Panel */}
                <div className="oc-content-panel">
                    
                    {activeTab === 'duration' && (
                        <div className="oc-config-grid fade-in">
                            {/* Left Box: Mode Calculation Selection Card */}
                            <div className="oc-card">
                                <div className="oc-card-header">
                                    <div className="oc-card-header-icon"><Target size={12} /></div>
                                    <span>Mode de calcul de la date limite</span>
                                </div>
                                <div className="oc-card-body">
                                    <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 12px 0', lineHeight: 1.3, fontWeight: 500 }}>
                                        Choisissez comment le système définit automatiquement le délai maximum accordé à une nouvelle recrue pour valider son parcours.
                                    </p>
                                    
                                    <div className="oc-formula-options">
                                        {[
                                            { value: 'GENERAL', label: 'Standard (Général)', hint: "Une durée fixe uniforme pour l'entreprise." },
                                            { value: 'PATH', label: 'Par Parcours d\'intégration', hint: 'Délai adapté selon le parcours assigné.' },
                                            { value: 'POSITION', label: 'Par Poste (Métier)', hint: 'Durée ajustée selon le poste occupé.' },
                                        ].map(opt => (
                                            <div
                                                key={opt.value}
                                                className={`oc-formula-option ${localSettings.durationMode === opt.value ? 'selected' : ''}`}
                                                onClick={() => handleModeChange(opt.value)}
                                            >
                                                <div className="oc-formula-radio">
                                                    <div className="oc-formula-radio-dot" />
                                                </div>
                                                <div className="oc-formula-text">
                                                    <span className="oc-formula-name">{opt.label}</span>
                                                    <span className="oc-formula-hint">{opt.hint}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Right Box: Rules Setup Card with Search Filters */}
                            <div className="oc-card">
                                <div className="oc-card-header">
                                    <div className="oc-card-header-icon"><Clock size={12} /></div>
                                    <span>Délais applicables</span>
                                </div>
                                <div className="oc-card-body">
                                    {localSettings.durationMode === 'GENERAL' && (
                                        <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 2px 0', lineHeight: 1.3 }}>
                                                Configurez la durée globale (en jours) qui sera assignée par défaut à tous les nouveaux collaborateurs.
                                            </p>
                                            
                                            <div className="oc-mini-field">
                                                <span className="oc-mini-label">Durée par défaut</span>
                                                <div className="oc-mini-input-group" style={{ maxWidth: '160px' }}>
                                                    <input 
                                                        type="number" 
                                                        value={localSettings.defaultDuration} 
                                                        onChange={(e) => setLocalSettings({...localSettings, defaultDuration: parseInt(e.target.value) || 0})}
                                                        className="oc-mini-input" 
                                                    />
                                                    <div className="oc-mini-input-tag">jours</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {localSettings.durationMode === 'PATH' && (
                                        <div className="fade-in">
                                            <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 10px 0', lineHeight: 1.3 }}>
                                                Renseignez le nombre de jours maximum accordé pour chacun de vos parcours d'intégration.
                                            </p>
                                            
                                            {/* Parcours search filter */}
                                            <div style={{ marginBottom: '8px', position: 'relative' }}>
                                                <Search size={11} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--oc-text-muted)' }} />
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher un parcours..."
                                                    value={ruleSearchQuery}
                                                    onChange={(e) => setRuleSearchQuery(e.target.value)}
                                                    className="oc-mini-input"
                                                    style={{ paddingLeft: '28px', fontSize: '11px' }}
                                                />
                                            </div>
                                            
                                            <div style={{ border: '1px solid var(--oc-border)', borderRadius: '6px', overflow: 'hidden', maxHeight: '250px', overflowY: 'auto' }}>
                                                {filteredParcours.length === 0 ? (
                                                    <div className="oc-empty" style={{ padding: '16px 12px' }}>
                                                        <div className="oc-empty-icon"><Target size={14} /></div>
                                                        <p className="oc-empty-title">Aucun parcours</p>
                                                        <p className="oc-empty-desc">Aucun parcours ne correspond à votre recherche.</p>
                                                    </div>
                                                ) : (
                                                    filteredParcours.map((p) => {
                                                        const rule = durationRules.find(r => r.parcours?.id === p.id);
                                                        return (
                                                            <div key={p.id} className="oc-list-row-item">
                                                                <span className="oc-row-title">{p.nom}</span>
                                                                <div style={{ width: '90px' }}>
                                                                    <div className="oc-mini-input-group">
                                                                        <input 
                                                                            type="number" 
                                                                            defaultValue={rule?.duration || 30} 
                                                                            onBlur={(e) => handleRuleSave('PATH', p.id, e.target.value)}
                                                                            className="oc-mini-input" 
                                                                        />
                                                                        <div className="oc-mini-input-tag" style={{ padding: '0 4px', fontSize: '9px' }}>J</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {localSettings.durationMode === 'POSITION' && (
                                        <div className="fade-in">
                                            <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 10px 0', lineHeight: 1.3 }}>
                                                Configurez le délai de validation accordé en fonction des responsabilités de chaque poste.
                                            </p>
                                            
                                            {/* Postes/Jobs search filter */}
                                            <div style={{ marginBottom: '8px', position: 'relative' }}>
                                                <Search size={11} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--oc-text-muted)' }} />
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher un poste..."
                                                    value={ruleSearchQuery}
                                                    onChange={(e) => setRuleSearchQuery(e.target.value)}
                                                    className="oc-mini-input"
                                                    style={{ paddingLeft: '28px', fontSize: '11px' }}
                                                />
                                            </div>
                                            
                                            <div style={{ border: '1px solid var(--oc-border)', borderRadius: '6px', overflow: 'hidden', maxHeight: '250px', overflowY: 'auto' }}>
                                                {filteredJobs.length === 0 ? (
                                                    <div className="oc-empty" style={{ padding: '16px 12px' }}>
                                                        <div className="oc-empty-icon"><Briefcase size={14} /></div>
                                                        <p className="oc-empty-title">Aucun poste</p>
                                                        <p className="oc-empty-desc">Aucun poste ne correspond à votre recherche.</p>
                                                    </div>
                                                ) : (
                                                    filteredJobs.map((job) => {
                                                        const rule = durationRules.find(r => r.job?.id === job.id);
                                                        return (
                                                            <div key={job.id} className="oc-list-row-item">
                                                                <span className="oc-row-title">{job.name}</span>
                                                                <div style={{ width: '90px' }}>
                                                                    <div className="oc-mini-input-group">
                                                                        <input 
                                                                            type="number" 
                                                                            defaultValue={rule?.duration || 30} 
                                                                            onBlur={(e) => handleRuleSave('POSITION', job.id, e.target.value)}
                                                                            className="oc-mini-input" 
                                                                        />
                                                                        <div className="oc-mini-input-tag" style={{ padding: '0 4px', fontSize: '9px' }}>J</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'alerts' && (
                        <div className="oc-alerts-layout fade-in">
                            {/* Left Box: Event Notification Blocks */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {alerts.map(alert => {
                                    const isEndOfPath = alert.event === 'END_OF_PATH';
                                    return (
                                        <div key={alert.id} className="oc-source-block is-active">
                                            <div className="oc-source-header">
                                                <div className={`oc-source-icon ${isEndOfPath ? 'manager' : 'collab'}`}>
                                                    {isEndOfPath ? <Target size={12} /> : <Layers size={12} />}
                                                </div>
                                                <div className="oc-source-title">
                                                    <span className="oc-source-name">
                                                        {isEndOfPath ? 'Fin du Parcours Complet' : 'Fin de chaque Module'}
                                                    </span>
                                                    <span className="oc-source-desc">
                                                        {isEndOfPath ? 'Notifie les destinataires dès que l\'intégration est complétée à 100%.' : 'Alerte émise après la finalisation d\'un module de formation.'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="oc-source-fields">
                                                <div className="oc-field-section">
                                                    <span className="oc-section-label">Destinataires à notifier</span>
                                                    <div className="oc-toggle-grid">
                                                        {[
                                                            { key: 'notifyManager', label: 'Manager' },
                                                            { key: 'notifyHR', label: 'RH' },
                                                            { key: 'notifyBuddy', label: 'Parrain' },
                                                        ].map(rec => (
                                                            <div key={rec.key} className="oc-toggle-row">
                                                                <span className="oc-toggle-label">{rec.label}</span>
                                                                <button
                                                                    className={`oc-source-toggle accent ${alert[rec.key] ? 'on' : ''}`}
                                                                    onClick={() => handleAlertToggle(alert.id, rec.key, !alert[rec.key])}
                                                                    type="button"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                
                                                <div className="oc-field-section">
                                                    <span className="oc-section-label">Canaux de communication</span>
                                                    <div className="oc-channel-pills">
                                                        <button
                                                            className={`oc-channel-pill ${alert.viaEmail ? 'active' : ''}`}
                                                            onClick={() => handleAlertToggle(alert.id, 'viaEmail', !alert.viaEmail)}
                                                        >
                                                            <Mail size={11} /> Email
                                                        </button>
                                                        <button
                                                            className={`oc-channel-pill ${alert.viaNotification ? 'active' : ''}`}
                                                            onClick={() => handleAlertToggle(alert.id, 'viaNotification', !alert.viaNotification)}
                                                        >
                                                            <Smartphone size={11} /> Application
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Right Box: Stagnation delay card */}
                            <div className="oc-card" style={{ alignSelf: 'start' }}>
                                <div className="oc-card-header">
                                    <div className="oc-card-header-icon"><Calendar size={12} /></div>
                                    <span>Relance de stagnation</span>
                                </div>
                                <div className="oc-card-body">
                                    <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 12px 0', lineHeight: 1.3 }}>
                                        Une relance automatique de relance d'activité sera émise vers l'accompagnateur si la recrue n'enregistre aucune action pendant cette durée.
                                    </p>
                                    
                                    <div className="oc-anon-option on">
                                        <div className="oc-anon-icon">
                                            <Clock size={14} />
                                        </div>
                                        <div className="oc-anon-text">
                                            <span className="oc-anon-title">Délai d'inactivité maximal</span>
                                            <span className="oc-anon-desc">Déclencher la relance après stagnation de :</span>
                                        </div>
                                        <div style={{ width: '85px', flexShrink: 0 }}>
                                            <div className="oc-mini-input-group">
                                                <input 
                                                    type="number" 
                                                    value={localSettings.stagnationDelay} 
                                                    onChange={(e) => setLocalSettings({...localSettings, stagnationDelay: parseInt(e.target.value) || 0})}
                                                    className="oc-mini-input" 
                                                />
                                                <div className="oc-mini-input-tag" style={{ padding: '0 4px', fontSize: '9px' }}>J</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'equipment' && (
                        <div className="oc-split-layout fade-in">
                            {/* Inner Split Left: Kits list sidebar */}
                            <div className="oc-sidebar">
                                <div className="oc-sidebar-search">
                                    <Search size={12} className="oc-sidebar-search-icon" />
                                    <input 
                                        type="text" 
                                        placeholder="Filtrer..." 
                                        value={kitSearchQuery}
                                        onChange={(e) => setKitSearchQuery(e.target.value)}
                                        className="oc-sidebar-input"
                                    />
                                </div>
                                <div className="oc-sidebar-list">
                                    {filteredKits.length === 0 ? (
                                        <div className="oc-empty" style={{ padding: '16px 4px' }}>
                                            <p className="oc-empty-title" style={{ fontSize: '10px' }}>Aucun kit</p>
                                        </div>
                                    ) : (
                                        filteredKits.map(kit => (
                                            <div 
                                                key={kit.id} 
                                                className={`oc-user-item ${selectedKitId === kit.id ? 'selected' : ''}`}
                                                onClick={() => setSelectedKitId(kit.id)}
                                            >
                                                <div className="oc-user-avatar">
                                                    {kit.name ? kit.name.substring(0, 2) : 'KT'}
                                                </div>
                                                <div className="oc-user-info">
                                                    <span className="oc-user-name">{kit.name}</span>
                                                    <span className="oc-user-job">{kit.responsible || 'Responsable'}</span>
                                                </div>
                                                <ChevronRight size={12} className="oc-user-arrow" />
                                            </div>
                                        ))
                                    )}
                                </div>
                                
                                {/* Sidebar footer creation button */}
                                <div style={{ padding: '8px', borderTop: '1px solid var(--oc-border)', background: '#fafafa' }}>
                                    <button className="oc-btn oc-btn-ghost" style={{ width: '100%', justifyContent: 'center', padding: '6px' }} onClick={handleAddKit}>
                                        <Plus size={12} />
                                        <span>Créer un kit</span>
                                    </button>
                                </div>
                            </div>

                            {/* Inner Split Right: Kit detailed editor panel */}
                            <div className="oc-consult-panel">
                                {!selectedKit ? (
                                    <div className="oc-empty" style={{ padding: '48px 12px' }}>
                                        <div className="oc-empty-icon"><Laptop size={20} /></div>
                                        <h3 className="oc-empty-title">Aucun Kit Sélectionné</h3>
                                        <p className="oc-empty-desc">Veuillez sélectionner un kit logistique dans la liste ou en créer un nouveau.</p>
                                    </div>
                                ) : (
                                    <div className="fade-in">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f3f4f6', paddingBottom: '8px', marginBottom: '12px' }}>
                                            <div>
                                                <span style={{ fontSize: '9px', fontWeight: 800, color: 'var(--oc-accent)', textTransform: 'uppercase', tracking: '0.04em' }}>
                                                    ÉDITION DU KIT LOGISTIQUE
                                                </span>
                                                <h3 style={{ margin: '1px 0 0 0', color: 'var(--oc-primary)', fontWeight: 800, fontSize: '15px' }}>
                                                    {selectedKit.name}
                                                </h3>
                                            </div>
                                            <button 
                                                onClick={() => handleDeleteKit(selectedKit.id)}
                                                className="oc-btn oc-btn-danger"
                                                style={{ padding: '4px 10px', borderRadius: '4px' }}
                                            >
                                                <Trash2 size={11} />
                                                <span>Supprimer</span>
                                            </button>
                                        </div>
                                        
                                        <div className="oc-form-field">
                                            <label>Nom de l'équipement / kit</label>
                                            <input 
                                                type="text"
                                                value={selectedKit.name} 
                                                onChange={(e) => handleKitFieldChange('name', e.target.value)}
                                                className="oc-input-text" 
                                            />
                                        </div>
                                        
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                            <div className="oc-form-field">
                                                <label>Responsable de préparation</label>
                                                <input 
                                                    type="text"
                                                    value={selectedKit.responsible || ''} 
                                                    onChange={(e) => handleKitFieldChange('responsible', e.target.value)}
                                                    className="oc-input-text" 
                                                    placeholder="Ex: Équipe IT / Services Généraux"
                                                />
                                            </div>
                                            <div className="oc-form-field">
                                                <label>Email de contact</label>
                                                <input 
                                                    type="email"
                                                    value={selectedKit.email} 
                                                    onChange={(e) => handleKitFieldChange('email', e.target.value)}
                                                    className="oc-input-text" 
                                                    placeholder="Ex: support@votreentreprise.com"
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="oc-form-field">
                                            <label>Matériels & Comptes à fournir (séparés par virgule)</label>
                                            <textarea 
                                                value={selectedKit.items} 
                                                onChange={(e) => handleKitFieldChange('items', e.target.value)}
                                                className="oc-textarea"
                                                placeholder="Ex: PC Portable, Badge d'accès, Licences"
                                            />
                                        </div>
                                        
                                        <div className="oc-items-wrapper">
                                            <h5>Aperçu des éléments</h5>
                                            <div className="oc-items-list">
                                                {selectedKit.items ? (
                                                    selectedKit.items.split(',').map((item, idx) => {
                                                        const trimmedItem = item.trim();
                                                        if (!trimmedItem) return null;
                                                        return (
                                                            <div key={idx} className="oc-item-pill">
                                                                <CheckCircle2 size={10} style={{ color: 'var(--oc-primary)' }} />
                                                                <span>{trimmedItem}</span>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <span style={{ fontSize: '10px', color: '#9ca3af', fontStyle: 'italic' }}>
                                                        Aucun élément à préparer renseigné.
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    
                </div>
            </div>
        </div>
    );
};

export default OnboardingConfiguration;
